
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { css } from '@emotion/react';
import { config } from '@config/pendo.config.json';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import CredlySettings from 'institutions/components/credly-settings';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownTextItem } from 'shared/components/inputs/nv-dropdown';

import { black } from 'styles/global_defaults/colors';
import { doubleSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';

import AccredibleSettings from 'institutions/components/advanced-settings/integrations/digital-credential/accredible-settings';

type Props = {
  show: boolean;
  onClose: () => void;
};

const DigitalCredentialsIntegration = ({ show, onClose }: Props) => {
  const styles = css`
    .nv-dropdown {
      width: 300px;
      margin: auto;
    }

    .bs4-dropdown-item {
      color: ${black};
    }
  `;

  const modalStyles = css`
    .bs4-modal-body {
      padding: ${doubleSpacing}px ${tripleSpacing}px;
    }
  `;

  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [showCredlyModal, setShowCredlyModal] = useState(false);
  const [showAccredibleModal, setShowAccredibleModal] = useState(false);

  useEffect(() => {
    if (!show) {
      setSelectedProvider(null);
    }
  }, [show]);

  const dropdownItems: NvDropdownTextItem[] = [
    {
      id: 'accredible',
      type: 'text',
      text: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.TITLE(),
      callback: () => setSelectedProvider('accredible'),
      dataQa: config.pendo.digitalCredential.accredibleIntegrationOption,
    },
    {
      id: 'credly',
      type: 'text',
      text: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.TITLE(),
      callback: () => setSelectedProvider('credly'),
      dataQa: config.pendo.digitalCredential.credlyIntegrationOption,
    },
  ];

  const selectedLabel = dropdownItems.find((p) => p.id === selectedProvider)?.text
    || t.INSTITUTIONS.ADVANCED_SETTINGS.DIGITAL_CREDENTIAL_INTEGRATIONS.DIGITAL_CREDENTIAL_MODAL.SELECT_PROVIDER();

  const handleNext = () => {
    if (selectedProvider === 'credly') {
      onClose();
      setShowCredlyModal(true);
    } else if (selectedProvider === 'accredible') {
      onClose();
      setShowAccredibleModal(true);
    }
  };

  return (
    <React.Fragment>
      <NvModal
        show={show}
        onClose={onClose}
        type={ModalType.FIXED}
        modalStyles={modalStyles}
        height='auto'
        data-qa={config.pendo.digitalCredential.integrationModal}
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.DIGITAL_CREDENTIAL_INTEGRATIONS.TITLE()}
        body={(
          <div css={styles}>
            <div className='mb-6'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.DIGITAL_CREDENTIAL_INTEGRATIONS.DIGITAL_CREDENTIAL_MODAL.MESSAGE()}
            </div>
            <NvDropdown
              items={dropdownItems}
              buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
              minWidth={300}
              title={selectedLabel}
              align={NvDropdownAlign.CENTER}
              titleClass={selectedProvider ? '' : 'text-gray-2'}
              toggleDataQa={config.pendo.digitalCredential.selectIntegrationOption}
            />

            <div className='d-flex justify-content-center mt-8'>
              <Button
                variant='secondary'
                onClick={onClose}
                data-qa={config.pendo.digitalCredential.digitalCredentialCancel}
              >
                {t.FORM.CANCEL()}
              </Button>

              <Button
                variant='primary'
                disabled={!selectedProvider}
                onClick={handleNext}
                className='ml-2'
                data-qa={config.pendo.digitalCredential.digitalCredentialNext}
              >
                {t.FORM.NEXT()}
              </Button>
            </div>
          </div>
        )}
      />

      <CredlySettings
        show={showCredlyModal}
        onClose={() => setShowCredlyModal(false)}
      />

      <AccredibleSettings
        show={showAccredibleModal}
        onClose={() => setShowAccredibleModal(false)}
      />
    </React.Fragment>
  );
};

export default DigitalCredentialsIntegration;
